import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';
import { ICONS } from '../../config/logoConfig';

const Sidebar = ({
  logoSrc = '/api/placeholder/150/60',
  logoAlt = 'Drive360 Logo',
  onLogout = () => {},
  onVehicleTypeSelect,
  selectedVehicleId,
  style = {},
  menuItems = [
    { icon: '⬜', label: 'DASHBOARD' },
    { icon: '⚙️', label: 'SETTINGS' },
    { icon: '📄', label: 'REPORT' }
  ]
}) => {
  const navigate = useNavigate();
  const location = useLocation(); // Get current location/path
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedVehicleName, setSelectedVehicleName] = useState('Mobile Equipment Type');
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);
  const menuRef = useRef(null);
  const initialLoadDone = useRef(false);
  
  // Check if we're on the main dashboard page
  const isHomePage = location.pathname === '/' || 
                    location.pathname === '' || 
                    location.pathname === '/home' ||
                    location.pathname === '/dashboard' ||
                    location.pathname === '/main';
  console.log('Current path:', location.pathname);
  console.log('Is main dashboard page:', isHomePage);

  useEffect(() => {
    // Close dropdown when navigating away from main dashboard page
    if (!isHomePage) {
      setIsDropdownOpen(false);
    }
  }, [location.pathname, isHomePage]);

  useEffect(() => {
    // Fetch vehicle types when component mounts or vehicle ID changes
    const fetchVehicleTypes = async () => {
      try {
        // Get admin's locationId from localStorage
        const user = JSON.parse(localStorage.getItem('user'));
        const locationId = user?.locationId;
        let url = 'http://eframeehs.in:3001/api/vehicle-types';
        if (locationId) {
          url += `?locationId=${locationId}`;
        }
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch vehicle types');
        const data = await response.json();
        setVehicleTypes(data);
        
        // Set the initial selected vehicle name if a vehicleId is provided
        if (selectedVehicleId && data.length > 0) {
          const selectedVehicle = data.find(vehicle => vehicle.VehicleID.toString() === selectedVehicleId.toString());
          if (selectedVehicle) {
            setSelectedVehicleName(selectedVehicle.ScreenName || selectedVehicle.VehicleName || `Vehicle ${selectedVehicle.VehicleID}`);
            
            // Only prepare data on initial load, not during re-renders
            if (!initialLoadDone.current) {
              initialLoadDone.current = true;
              prepareVehicleData(selectedVehicle.VehicleID);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching vehicle types:', error);
        // Return an empty array if the API fails
        setVehicleTypes([]);
      }
    };

    fetchVehicleTypes();
  }, [selectedVehicleId]); // Remove isLoading from dependencies

  useEffect(() => {
    // Handle clicks outside the dropdown to close it
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Update menu position when dropdown opens/closes
    if (menuRef.current) {
      if (isDropdownOpen) {
        menuRef.current.classList.add('dropdown-open');
        menuRef.current.style.setProperty('--dropdown-height', `${dropdownHeight}px`);
      } else {
        menuRef.current.classList.remove('dropdown-open');
      }
    }
  }, [isDropdownOpen, dropdownHeight]);

  useEffect(() => {
    // Set dropdown height for animations
    if (dropdownMenuRef.current) {
      if (isDropdownOpen && vehicleTypes.length > 0) {
        // We'll use a fixed height per item approach with a max height
        const itemHeight = 45; // approximate height of each dropdown item in pixels
        const maxVisibleItems = 5; // Maximum number of items to show before capping the height
        const calculatedHeight = Math.min(vehicleTypes.length, maxVisibleItems) * itemHeight;
        setDropdownHeight(calculatedHeight);
      } else {
        setDropdownHeight(0);
      }
    }
  }, [isDropdownOpen, vehicleTypes.length]);

  const toggleDropdown = () => {
    // Allow dropdown toggle regardless of the page
    const newState = !isDropdownOpen;
    setIsDropdownOpen(newState);
  };

  const prepareVehicleData = async (vehicleId) => {
    // Skip preparing data for empty vehicle ID
    if (!vehicleId) return;
    
    try {
      // Only set loading state if not already loading
      if (!isLoading) {
        setIsLoading(true);
      }
      
      // Check if this API endpoint exists
      try {
        const response = await fetch(`http://eframeehs.in:3001/api/prepare-vehicle-data/${vehicleId}`);
        if (!response.ok) {
          console.log(`API endpoint not available, skipping data preparation for vehicle ${vehicleId}`);
          // If API doesn't exist, just continue without error
          return;
        }
        await response.json();
        console.log(`Data prepared for vehicle ID ${vehicleId}`);
      } catch (error) {
        // Silently handle if endpoint doesn't exist
        console.log('Skipping vehicle data preparation - API may not be implemented');
      }
    } catch (error) {
      console.error('Error preparing vehicle data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVehicleSelect = async (vehicle) => {
    // First update UI immediately for better user experience
    const vehicleName = vehicle.ScreenName || vehicle.VehicleName || `Vehicle ${vehicle.VehicleID}`;
    setSelectedVehicleName(vehicleName);
    setIsDropdownOpen(false);
    
    // Then update the parent component with the new vehicle ID (converted to string)
    onVehicleTypeSelect(vehicle.VehicleID.toString());
    
    // Prepare data in the background (non-blocking)
    try {
      setIsLoading(true);
      await prepareVehicleData(vehicle.VehicleID);
    } catch (error) {
      console.error('Error selecting vehicle:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMenuClick = (menuItem) => {
    if (menuItem.onClick) {
      menuItem.onClick(menuItem.label);
    } else {
      alert(`Navigating to ${menuItem.label}`);
    }
  };

  const navigateToDriverSegmentation = () => {
    navigate('/driver-segmentation');
  };

  const navigateToTrendAnalysis = () => {
    navigate('/trend-analysis');
  };

  const navigateToUserWiseReport = () => {
    navigate('/user-wise-report');
  };

  const navigateToSettings = () => {
    navigate('/settings');
  };

  const navigateToHome = () => {
    navigate('/');
  };

  return (
    <div 
      className={`sidebar ${isHomePage ? 'home-page' : ''}`}
      style={{ 
        ...style,
        border: 'none', 
        borderLeft: 'none !important',
        boxShadow: 'none',
        outline: 'none'
      }}
    >
      <div className="sidebar-home-item" onClick={navigateToHome}>
        <div className="menu-item">
          <img src={ICONS.NAVIGATION.HOME} alt="Home Icon" className="menu-icon report-icon" />
          <span className="menu-text">Home</span>
        </div>
      </div>
      
      {isHomePage && (
        <div className="sidebar-section">
          <div className="section-label">Mobile Equipment Type</div>
          <div className="vehicle-dropdown-container" ref={dropdownRef}>
            <div className={`vehicle-dropdown-header ${isDropdownOpen ? 'open' : ''}`} onClick={toggleDropdown}>
              <span className="dropdown-text">
                {isLoading ? 'Loading...' : (
                  selectedVehicleName !== 'Mobile Equipment Type' ?
                  <>
                    {selectedVehicleName.toLowerCase().includes('forklift') && (
                      <img src={ICONS.VEHICLES.FORKLIFT} alt="Forklift Icon" className="vehicle-icon" />
                    )}
                    {selectedVehicleName.toLowerCase().includes('mtv') && (
                      <img src={ICONS.VEHICLES.MTV_WHITE} alt="MTV Icon" className="vehicle-icon" />
                    )}
                    {selectedVehicleName}
                  </> :
                  'Mobile Equipment Type'
                )}
              </span>
              <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
            </div>
            
            <div 
              className="vehicle-dropdown-menu" 
              ref={dropdownMenuRef}
              style={{ 
                maxHeight: isDropdownOpen ? `${dropdownHeight}px` : '0',
                height: isDropdownOpen ? 'auto' : '0', 
                visibility: isDropdownOpen ? 'visible' : 'hidden',
                opacity: isDropdownOpen ? 1 : 0,
                overflow: 'hidden'
              }}
            >
              <div className="dropdown-items-container" style={{ overflow: 'hidden' }}>
                {isDropdownOpen && vehicleTypes.map((vehicle) => (
                  <div 
                    key={vehicle.VehicleID} 
                    className="vehicle-dropdown-item"
                    onClick={() => handleVehicleSelect(vehicle)}
                  >
                    {vehicle.VehicleName.toLowerCase().includes('forklift') && (
                      <img src={ICONS.VEHICLES.FORKLIFT} alt="Forklift Icon" className="vehicle-icon" />
                    )}
                    {vehicle.VehicleName.toLowerCase().includes('mtv') && (
                      <img src={ICONS.VEHICLES.MTV_WHITE} alt="MTV Icon" className="vehicle-icon" />
                    )}
                    <span>{vehicle.ScreenName || vehicle.VehicleName}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="menu" ref={menuRef}>
        <div className="menu-section">
          <div className="menu-item" onClick={navigateToDriverSegmentation}>
            <img src={ICONS.NAVIGATION.REPORT} alt="Chart Icon" className="menu-icon report-icon" />
            <span className="menu-text">Driver Segmentation</span>
          </div>
          <div className="menu-item" onClick={navigateToTrendAnalysis}>
            <img src={ICONS.NAVIGATION.REPORT} alt="Report Icon" className="menu-icon report-icon" />
            <span className="menu-text">Trend Analysis Report</span>
          </div>
          <div className="menu-item" onClick={navigateToUserWiseReport}>
            <img src={ICONS.NAVIGATION.REPORT} alt="Report Icon" className="menu-icon report-icon" />
            <span className="menu-text">User Wise Report</span>
          </div>
          <div className="menu-item" onClick={navigateToSettings}>
            <img src={ICONS.NAVIGATION.SETTINGS} alt="Settings Icon" className="menu-icon report-icon" />
            <span className="menu-text">Settings</span>
          </div>
        </div>
      </div>
      
      {/* <div className="logout-btn" onClick={onLogout}>
        <span className="logout-icon">⚪</span>
        <span>Log out</span>
      </div> */}
    </div>
  );
};

Sidebar.propTypes = {
  logoSrc: PropTypes.string,
  logoAlt: PropTypes.string,
  onLogout: PropTypes.func,
  onVehicleTypeSelect: PropTypes.func.isRequired,
  selectedVehicleId: PropTypes.string,
  style: PropTypes.object,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func
    })
  )
};

export default Sidebar;